/* ============================================================================
   MM CAREERS — Ashby job index
   ----------------------------------------------------------------------------
   Structural differences from the standalone prototype, all Shopify-driven:

   1. SECTION-SCOPED, NOT DOCUMENT-SCOPED. Everything is looked up inside the
      section root. Sections can be duplicated in the theme editor, so global
      getElementById would make two instances fight over the same nodes.

   2. LIFECYCLE. Instances register for shopify:section:load / :unload and tear
      themselves down. The live header.liquid rebinds listeners on every
      section:load without removing the old ones; this does not repeat that.

   3. CONFIG VIA DATA ATTRIBUTES, set from the schema. No values are hardcoded
      here, so the file stays static and cacheable across themes.

   4. #ashby_embed SINGLETON. Ashby requires that exact element id. Two section
      instances would produce duplicate ids and Ashby would target the first.
      Instances past the first disable their embed and warn.
   ============================================================================ */
(function(){
"use strict";

var INSTANCES = [];

/* ---------------------------------------------------------------------------
   SANITISER — SECURITY CRITICAL

   descriptionHtml is recruiter-authored HTML from a third-party system being
   injected into the same origin as the storefront and its checkout. It is
   never passed to innerHTML raw.

   DOMParser produces a fully detached document: no scripts execute and no
   subresources are fetched during parsing. We then copy across an explicit
   allowlist of elements and attributes.
   --------------------------------------------------------------------------- */
var ALLOWED = {
  P:1, BR:1, HR:1, STRONG:1, B:1, EM:1, I:1, U:1, S:1, SUP:1, SUB:1,
  UL:1, OL:1, LI:1, BLOCKQUOTE:1, CODE:1, PRE:1, SPAN:1, DIV:1, A:1,
  H1:1, H2:1, H3:1, H4:1, H5:1, H6:1, TABLE:1, THEAD:1, TBODY:1, TR:1, TH:1, TD:1
};
var DANGEROUS = {
  SCRIPT:1, STYLE:1, IFRAME:1, OBJECT:1, EMBED:1, SVG:1, MATH:1, FORM:1,
  TEMPLATE:1, INPUT:1, BUTTON:1, LINK:1, META:1, BASE:1
};
var SAFE_PROTO = /^(https?:|mailto:|tel:)/i;

function sanitize(html, allowImages){
  var out = document.createDocumentFragment();
  if (typeof html !== "string" || !html) return out;

  var doc;
  try { doc = new DOMParser().parseFromString(html, "text/html"); }
  catch(e){ return out; }

  function walk(src, dest){
    var node = src.firstChild, next;
    while (node){
      next = node.nextSibling;

      if (node.nodeType === 3){
        dest.appendChild(document.createTextNode(node.nodeValue));

      } else if (node.nodeType === 1){
        var tag = node.tagName;

        /* Images are opt-in. Recruiters can attach them in Ashby's editor, so
           dropping them silently loses content — but an unrestricted src is an
           SSRF/tracking vector, hence the https-only check. */
        if (tag === "IMG" && allowImages){
          var src2 = node.getAttribute("src") || "";
          if (/^https:/i.test(src2)){
            var img = document.createElement("img");
            img.setAttribute("src", src2);
            img.setAttribute("alt", node.getAttribute("alt") || "");
            img.setAttribute("loading", "lazy");
            img.setAttribute("referrerpolicy", "no-referrer");
            dest.appendChild(img);
          }

        } else if (ALLOWED[tag]){
          var el = document.createElement(tag);
          /* Per-tag attribute allowlist. Drops every on* handler, style,
             srcset and data-* by construction rather than by blocklist. */
          if (tag === "A"){
            var href = node.getAttribute("href") || "";
            if (SAFE_PROTO.test(href)){
              el.setAttribute("href", href);
              el.setAttribute("rel", "noopener noreferrer nofollow");
              el.setAttribute("target", "_blank");
            }
          }
          walk(node, el);
          dest.appendChild(el);

        } else if (!DANGEROUS[tag]){
          walk(node, dest);          // unknown wrapper: unwrap, keep contents
        }
        /* Dangerous tags dropped entirely, children included. */
      }
      node = next;
    }
  }

  walk(doc.body, out);
  return out;
}

/* --------------------------------------------------------------------------- */
var EMPLOYMENT = { FullTime:"Full time", PartTime:"Part time", Intern:"Internship",
                   Contract:"Contract", Temporary:"Temporary" };
var WORKPLACE  = { OnSite:"On site", Remote:"Remote", Hybrid:"Hybrid" };
var UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function txt(v){ return (v === null || v === undefined) ? "" : String(v); }
function el(t,c,s){ var n=document.createElement(t); if(c)n.className=c;
                    if(s!==undefined)n.textContent=s; return n; }
function slugify(s){
  return txt(s).toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-+|-+$/g,"").slice(0,60);
}

/* ===========================================================================
   INSTANCE
   =========================================================================== */
function Careers(root){
  this.root = root;
  this.cfg = {
    board:        root.dataset.board || "",
    compensation: root.dataset.compensation === "true",
    embed:        root.dataset.embed === "true",
    embedCss:     root.dataset.embedCss || "",
    utmSource:    root.dataset.utmSource || "",
    exclude:      (root.dataset.exclude || "").split(",")
                    .map(function(s){ return s.trim().toLowerCase(); })
                    .filter(Boolean),
    allowImages:  root.dataset.allowImages === "true",
    timeout:      parseInt(root.dataset.timeout, 10) || 8000
  };

  this.all = [];
  this.filters = { department:null, workplaceType:null, employmentType:null };
  this.loadedOk = false;
  this.lastHandle = null;
  this.embedState = { injected:false, loadedId:null, timer:null };
  this.abort = null;

  this.$ = {};
  ["spec","count","label","filters","skel","results","empty","error","errorLink",
   "detail","detailTitle","detailBlock","detailBody","back","apply","applyLabel",
   "note","embed","embedStatus","embedFallback","embedFallbackLink","index"]
  .forEach(function(k){
    this.$[k] = root.querySelector('[data-mm="' + k + '"]');
  }, this);

  /* Ashby's target id must be unique in the document. */
  this.canEmbed = this.cfg.embed;
  if (this.canEmbed && document.querySelectorAll("#ashby_embed").length > 1){
    console.warn("[mm-careers] more than one #ashby_embed on the page — " +
                 "the application form embed is disabled for this instance. " +
                 "Only one careers section per page can host the form.");
    this.canEmbed = false;
  }

  this.onPop = this.route.bind(this);
  window.addEventListener("popstate", this.onPop);
  if (this.$.back) this.$.back.addEventListener("click", this.goIndex.bind(this));

  this.load();
}

Careers.prototype.destroy = function(){
  window.removeEventListener("popstate", this.onPop);
  clearTimeout(this.embedState.timer);
  if (this.abort) { try { this.abort.abort(); } catch(e){} }
};

/* ---- LOAD ---------------------------------------------------------------- */
Careers.prototype.load = function(){
  var self = this;
  if (!this.cfg.board){
    console.warn("[mm-careers] no Ashby board slug set in the section settings.");
    return this.ingest(null, false);
  }

  var url = "https://api.ashbyhq.com/posting-api/job-board/" +
            encodeURIComponent(this.cfg.board) +
            (this.cfg.compensation ? "?includeCompensation=true" : "");

  this.abort = (typeof AbortController !== "undefined") ? new AbortController() : null;

  fetch(url, {
    method:"GET", mode:"cors", credentials:"omit",
    signal: this.abort ? this.abort.signal : undefined
  })
  .then(function(r){ if(!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
  .then(function(d){ self.ingest(d, true); })
  .catch(function(err){
    if (err && err.name === "AbortError") return;
    console.warn("[mm-careers] Ashby fetch failed:", err);
    self.ingest(null, false);
  });
};

Careers.prototype.ingest = function(data, ok){
  var self = this;
  this.loadedOk = ok;
  var raw = (data && Array.isArray(data.jobs)) ? data.jobs : [];

  this.all = raw.filter(function(j){
    /* SECURITY CRITICAL — isListed:false means "reachable by direct link only".
       Rendering it publishes a posting recruiters deliberately withheld. Strict
       === true, so a missing field cannot default to published. */
    if (j.isListed !== true) return false;
    return self.cfg.exclude.indexOf(txt(j.department).toLowerCase()) === -1;
  }).map(function(j){
    /* The public payload carries no posting id, only URLs. The embed needs one.
       Live Ashby boards use {board}/{uuid} and {board}/{uuid}/application. */
    var m = UUID_RE.exec(txt(j.jobUrl)) || UUID_RE.exec(txt(j.applyUrl));
    var tail = m ? m[0].replace(/-/g,"").slice(-6) : "";
    return {
      title:          txt(j.title) || "Untitled role",
      department:     txt(j.department) || "Other",
      team:           txt(j.team),
      location:       txt(j.location) || "—",
      workplaceType:  txt(j.workplaceType),
      employmentType: txt(j.employmentType),
      applyUrl:       txt(j.applyUrl) || txt(j.jobUrl),
      jobUrl:         txt(j.jobUrl),
      compensation:   (j.compensation && j.compensation.compensationTierSummary) || "",
      body:           sanitize(j.descriptionHtml, self.cfg.allowImages),
      postingId:      m ? m[0] : null,
      handle:         (slugify(j.title) || "role") + (tail ? "-" + tail : "")
    };
  });

  if (this.$.skel) this.$.skel.hidden = true;
  if (this.$.errorLink && this.cfg.board){
    this.$.errorLink.href = "https://jobs.ashbyhq.com/" + encodeURIComponent(this.cfg.board);
  }
  this.buildFilters();
  this.route();
};

/* ---- FILTERS ------------------------------------------------------------- */
Careers.prototype.uniq = function(key){
  var seen = {}, out = [];
  this.all.forEach(function(j){ if (j[key] && !seen[j[key]]) { seen[j[key]]=1; out.push(j[key]); } });
  return out.sort();
};

Careers.prototype.buildFilters = function(){
  var wrap = this.$.filters, self = this;
  if (!wrap) return;
  wrap.textContent = "";

  var groups = [
    { key:"department",     label:"Department", fmt:function(v){ return v; } },
    { key:"workplaceType",  label:"Workplace",  fmt:function(v){ return WORKPLACE[v] || v; } },
    { key:"employmentType", label:"Contract",   fmt:function(v){ return EMPLOYMENT[v] || v; } }
  ];

  var any = false;
  groups.forEach(function(g){
    var values = self.uniq(g.key);
    /* A filter offering one option is noise. Single-site employers get no
       location filter, which is the point. */
    if (values.length < 2) return;
    any = true;

    var grp = el("div","mm-c-fgroup");
    grp.appendChild(el("span","mm-c-mono mm-c-key", g.label));
    var chips = el("div","mm-c-chips");

    var all = el("button","mm-c-chip","All");
    all.type="button"; all.setAttribute("aria-pressed","true"); all.dataset.all=g.key;
    all.addEventListener("click", function(){ self.setFilter(g.key,null); });
    chips.appendChild(all);

    values.forEach(function(v){
      var b = el("button","mm-c-chip", g.fmt(v));
      b.type="button"; b.setAttribute("aria-pressed","false");
      b.dataset.key=g.key; b.dataset.value=v;
      b.addEventListener("click", function(){
        self.setFilter(g.key, self.filters[g.key] === v ? null : v);
      });
      chips.appendChild(b);
    });

    grp.appendChild(chips);
    wrap.appendChild(grp);
  });

  wrap.hidden = !any;
};

Careers.prototype.setFilter = function(key, value){
  var self = this;
  this.filters[key] = value;
  Array.prototype.forEach.call(this.$.filters.querySelectorAll(".mm-c-chip"), function(b){
    if (b.dataset.all) b.setAttribute("aria-pressed", self.filters[b.dataset.all] ? "false" : "true");
    else b.setAttribute("aria-pressed", self.filters[b.dataset.key] === b.dataset.value ? "true" : "false");
  });
  this.renderIndex();
};

Careers.prototype.visible = function(){
  var f = this.filters;
  return this.all.filter(function(j){
    return (!f.department     || j.department     === f.department) &&
           (!f.workplaceType  || j.workplaceType  === f.workplaceType) &&
           (!f.employmentType || j.employmentType === f.employmentType);
  });
};

/* ---- RENDER: INDEX ------------------------------------------------------- */
Careers.prototype.renderIndex = function(){
  var jobs = this.visible(), self = this;
  this.$.results.textContent = "";

  if (this.$.count) this.$.count.textContent = jobs.length;
  if (this.$.label) this.$.label.textContent = jobs.length === 1 ? "role shown" : "roles shown";

  /* A healthy board with no open roles is not an error. */
  if (this.$.error) this.$.error.hidden = this.loadedOk;
  if (this.$.empty) this.$.empty.hidden = !(this.loadedOk && jobs.length === 0);
  if (!jobs.length) return;

  var byDept = {};
  jobs.forEach(function(j){ (byDept[j.department] = byDept[j.department] || []).push(j); });

  Object.keys(byDept).sort().forEach(function(dept){
    var sec = el("div","mm-c-dept");
    sec.appendChild(el("h3","mm-c-mono mm-c-dept__h", dept));
    var list = el("ul","mm-c-list");

    byDept[dept].sort(function(a,b){ return a.title.localeCompare(b.title); })
      .forEach(function(job){
        var li = el("li","mm-c-row");
        var btn = el("button","mm-c-row__btn");
        btn.type = "button";
        btn.dataset.handle = job.handle;      // focus-restoration anchor

        var main = el("div");
        main.appendChild(el("h4","mm-c-row__t", job.title));
        var meta = el("div","mm-c-mono mm-c-meta");
        [ job.location,
          WORKPLACE[job.workplaceType] || job.workplaceType,
          EMPLOYMENT[job.employmentType] || job.employmentType,
          job.team
        ].filter(Boolean).forEach(function(v){ meta.appendChild(el("span",null,v)); });
        main.appendChild(meta);

        btn.appendChild(main);
        btn.appendChild(el("span","mm-c-mono mm-c-row__go","View role"));
        btn.addEventListener("click", function(){
          self.lastHandle = job.handle;
          self.go(job.handle);
        });

        li.appendChild(btn);
        list.appendChild(li);
      });

    sec.appendChild(list);
    self.$.results.appendChild(sec);
  });
};

/* ---- RENDER: DETAIL ------------------------------------------------------ */
Careers.prototype.renderDetail = function(job){
  var self = this;
  this.$.detailTitle.textContent = job.title;

  var block = this.$.detailBlock;
  block.textContent = "";
  [ ["Department",   job.department],
    ["Team",         job.team],
    ["Location",     job.location],
    ["Workplace",    WORKPLACE[job.workplaceType] || job.workplaceType],
    ["Contract",     EMPLOYMENT[job.employmentType] || job.employmentType],
    ["Compensation", job.compensation]
  ].filter(function(p){ return p[1]; }).forEach(function(p){
    var c = el("div","mm-c-block__c");
    c.appendChild(el("span","mm-c-mono mm-c-key", p[0]));
    c.appendChild(el("span","mm-c-val", p[1]));
    block.appendChild(c);
  });

  this.$.detailBody.textContent = "";
  this.$.detailBody.appendChild(job.body.cloneNode(true));  // fragment is consumed on append

  var willEmbed = this.canEmbed && this.cfg.board && job.postingId;
  if (this.$.note){
    this.$.note.textContent = willEmbed
      ? "Takes about five minutes. Your details go straight to our hiring team."
      : "Opens our application form in a new tab.";
  }
  this.$.apply.onclick = function(){ self.openApply(job); };

  this.closeApply();
  this.embedState.loadedId = null;

  this.$.index.hidden = true;
  this.$.detail.hidden = false;
  this.root.scrollIntoView({ block:"start", behavior:"auto" });
  this.$.detailTitle.focus();
};

Careers.prototype.showIndex = function(){
  this.closeApply();
  this.$.detail.hidden = true;
  this.$.index.hidden = false;
  this.renderIndex();
  /* renderIndex rebuilds the list, so a held element reference would be stale.
     Re-find the row by handle instead. */
  if (this.lastHandle){
    var back = this.$.results.querySelector('[data-handle="' + this.lastHandle + '"]');
    if (back) back.focus();
    this.lastHandle = null;
  }
};

/* ---- APPLY / EMBED ------------------------------------------------------- */
Careers.prototype.withUtm = function(url){
  if (!url) return "#";
  if (!this.cfg.utmSource) return url;
  try {
    var u = new URL(url);
    if (!u.searchParams.has("utm_source")) u.searchParams.set("utm_source", this.cfg.utmSource);
    return u.toString();
  } catch(e){ return url; }
};

Careers.prototype.injectScript = function(){
  if (this.embedState.injected) return;
  this.embedState.injected = true;

  /* The theme editor re-runs section scripts on shopify:section:load. A second
     tag would clobber __Ashby and produce two iframes, so the existing one
     wins and we leave its settings untouched. */
  if (document.getElementById("ashby-script")) return;

  var s = {
    ashbyBaseJobBoardUrl: "https://jobs.ashbyhq.com/" + this.cfg.board,
    displayMode: "application-form-only",
    autoLoad: false,          // we call load() per role; Ashby's documented SPA pattern
    autoScroll: false,
    verboseLogging: false
  };
  if (this.cfg.embedCss) s.customCssUrl = this.cfg.embedCss;

  window.__Ashby = window.__Ashby || {};
  window.__Ashby.settings = s;      // must exist before the script executes

  var tag = document.createElement("script");
  tag.id  = "ashby-script";
  tag.src = "https://jobs.ashbyhq.com/" + encodeURIComponent(this.cfg.board) + "/embed?version=2";
  tag.onerror = this.embedFailed.bind(this, "script blocked or unreachable");
  document.body.appendChild(tag);
};

Careers.prototype.embedFailed = function(reason){
  console.warn("[mm-careers] application form embed unavailable:", reason);
  clearTimeout(this.embedState.timer);
  if (this.$.embedStatus)   this.$.embedStatus.hidden = true;
  if (this.$.embedFallback) this.$.embedFallback.hidden = false;
};

Careers.prototype.openApply = function(job){
  var self = this;

  /* No embed, no board, or no derivable posting id: don't pretend. Send them
     to the page that definitely works. */
  if (!(this.canEmbed && this.cfg.board && job.postingId)){
    if (this.canEmbed && !job.postingId){
      console.warn("[mm-careers] no jobPostingId derivable from", job.jobUrl);
    }
    window.open(this.withUtm(job.applyUrl), "_blank", "noopener,noreferrer");
    return;
  }

  this.$.embedFallbackLink.href = this.withUtm(job.applyUrl);
  this.$.apply.hidden = true;
  if (this.$.note) this.$.note.hidden = true;
  this.$.embed.hidden = false;

  if (this.embedState.loadedId === job.postingId) return;
  this.$.embedStatus.hidden = false;
  this.$.embedFallback.hidden = true;

  this.injectScript();

  /* Ashby defines __Ashby.iFrame asynchronously — poll rather than guess a delay. */
  var deadline = Date.now() + this.cfg.timeout;
  (function wait(){
    var api = (window.__Ashby && typeof window.__Ashby.iFrame === "function")
              ? window.__Ashby.iFrame() : null;
    if (api && typeof api.load === "function"){
      try {
        api.load({ jobPostingId: job.postingId });
        self.embedState.loadedId = job.postingId;
        self.watchFrame(deadline);
      } catch(e){ self.embedFailed((e && e.message) || "load() threw"); }
      return;
    }
    if (Date.now() > deadline) return self.embedFailed("iFrame() never appeared");
    setTimeout(wait, 120);
  })();
};

/* load() resolving is not proof the form rendered. Confirm a frame exists. */
Careers.prototype.watchFrame = function(deadline){
  var self = this;
  clearTimeout(this.embedState.timer);
  (function check(){
    var frame = self.root.querySelector("#ashby_embed iframe");
    if (frame){
      /* Ashby owns the iframe, so patch the a11y gap from outside. */
      if (!frame.getAttribute("title")) frame.setAttribute("title","Job application form");
      self.$.embedStatus.hidden = true;
      return;
    }
    if (Date.now() > deadline) return self.embedFailed("iframe never rendered");
    self.embedState.timer = setTimeout(check, 200);
  })();
};

Careers.prototype.closeApply = function(){
  clearTimeout(this.embedState.timer);
  if (this.$.embed) this.$.embed.hidden = true;
  if (this.$.apply) this.$.apply.hidden = false;
  if (this.$.note)  this.$.note.hidden  = false;
};

/* ---- ROUTING — ?job=<handle>, real URLs, working back button ------------- */
Careers.prototype.go = function(handle){
  var url = new URL(window.location.href);
  if (handle) url.searchParams.set("job", handle); else url.searchParams.delete("job");
  history.pushState({ job:handle || null }, "", url.toString());
  this.route();
};
Careers.prototype.goIndex = function(){ this.go(null); };

Careers.prototype.route = function(){
  var h;
  try { h = new URL(window.location.href).searchParams.get("job"); } catch(e){ h = null; }
  var job = h && this.all.filter(function(j){ return j.handle === h; })[0];
  if (job) this.renderDetail(job); else this.showIndex();
};

/* ===========================================================================
   BOOTSTRAP
   =========================================================================== */
function initIn(scope){
  (scope || document).querySelectorAll("[data-mm-careers]").forEach(function(root){
    if (root.__mmCareers) return;                 // never double-initialise
    root.__mmCareers = new Careers(root);
    INSTANCES.push(root.__mmCareers);
  });
}

if (document.readyState === "loading"){
  document.addEventListener("DOMContentLoaded", function(){ initIn(document); });
} else {
  initIn(document);
}

/* Theme editor lifecycle. Teardown on unload is what stops listeners
   accumulating every time the section is edited or reordered. */
document.addEventListener("shopify:section:load", function(e){ initIn(e.target); });
document.addEventListener("shopify:section:unload", function(e){
  e.target.querySelectorAll("[data-mm-careers]").forEach(function(root){
    if (!root.__mmCareers) return;
    root.__mmCareers.destroy();
    INSTANCES = INSTANCES.filter(function(i){ return i !== root.__mmCareers; });
    delete root.__mmCareers;
  });
});

})();
